import { Card, CardContent, CardFooter, CardHeader } from "@/modern/components/ui/card-shadcn";
import { Button } from "@/modern/components/ui/button-shadcn";
import { X } from "lucide-react";
import { ReactNode, type SyntheticEvent } from "react";
import { cn } from "@/modern/utils";

const stopDragPointer = (e: SyntheticEvent) => {
  e.stopPropagation();
};

type DashboardCardType = {
  header: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  gridSpan?: string;
  cardContentClassName?: string;
  className?: string;
  headerClassName?: string;
  onClose?: () => void;
  onDragEnter?: (event: React.DragEvent) => void;
  onDragLeave?: (event: React.DragEvent) => void;
  onDragOver?: (event: React.DragEvent) => void;
  onDrop?: (event: React.DragEvent) => void;
};

export const WidgetCloseButton = ({ onClose }: { onClose: () => void }) => (
  <Button
    type="button"
    variant="ghost"
    size="icon"
    className="size-7 shrink-0"
    data-widget-close
    aria-label="Close"
    onPointerDown={stopDragPointer}
    onMouseDown={stopDragPointer}
    onTouchStart={stopDragPointer}
    onClick={(e) => {
      e.stopPropagation();
      onClose();
    }}
  >
    <X className="size-4 pointer-events-none" />
  </Button>
);

const DashboardCardRoot = ({
  children,
  gridSpan,
  className,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
}: Pick<
  DashboardCardType,
  "children" | "gridSpan" | "className" | "onDragEnter" | "onDragLeave" | "onDragOver" | "onDrop"
>) => {
  return (
    <Card
      variant="outline"
      className={cn("bg-card/30 rounded-md dark:bg-card/50 h-full flex flex-col col-span-12  @2xl:col-span-6  @5xl:col-span-4 border-none", gridSpan && `${gridSpan}`, className)}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {children}
    </Card>
  );
};

const DashboardCardContent = ({ children, className }: Pick<DashboardCardType, "children" | "className">) => {
  return <CardContent className={cn("flex-1 min-h-0 overflow-auto px-4 border-x border-b rounded-b-md", className)}>{children}</CardContent>;
};

const DashboardCardFooter = ({ children, className }: Pick<DashboardCardType, "children" | "className">) => {
  return <CardFooter className={cn("border-t min-h-[3.8rem] mt-auto pt-swivu-base", className)}>{children}</CardFooter>;
};

const DashboardCardHeader = ({ children, className, onClose }: Pick<DashboardCardType, "children" | "className" | "onClose">) => {
  return (
    <CardHeader className={cn("py-0 bg-card/50 dark:bg-card px-4 h-[3.25rem] border-b flex-none flex flex-row items-center justify-between space-y-0 rounded-t-md border", className)}>
      <div className="flex w-full items-center justify-between gap-2 min-w-0">
        <div className="flex-1 min-w-0">{children}</div>
        {onClose ? <WidgetCloseButton onClose={onClose} /> : null}
      </div>
    </CardHeader>
  );
};

export const DashboardCard = ({
  header,
  footer,
  children,
  gridSpan,
  cardContentClassName,
  className,
  headerClassName,
  onClose,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
}: DashboardCardType) => {
  return (
    <DashboardCardRoot
      gridSpan={gridSpan}
      className={className}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <DashboardCardHeader className={headerClassName} onClose={onClose}>{header}</DashboardCardHeader>
      <DashboardCardContent className={cardContentClassName}>{children}</DashboardCardContent>
      {footer && <DashboardCardFooter>{footer}</DashboardCardFooter>}
    </DashboardCardRoot>
  );
};

DashboardCard.Root = DashboardCardRoot;
DashboardCard.Content = DashboardCardContent;
DashboardCard.Footer = DashboardCardFooter;
DashboardCard.Header = DashboardCardHeader;
